import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isOptOutDecided, setOptedOut } from '../utils/ryanAnalytics';
import RyanAdminHeader from './components/RyanAdminHeader';
import RyanAdminSidebar, { RyanAdminPage as RyanAdminPageType } from './components/RyanAdminSidebar';
import RyanAdminStats from './components/tabs/RyanAdminStats';
import RyanAdminErrors from './components/tabs/RyanAdminErrors';
import RyanAdminHub from './components/tabs/RyanAdminHub';
import RyanAdminAccounts from './components/tabs/RyanAdminAccounts';
import './RyanAdminPage.css';

const RyanAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, loading } = useAuth();
  const [activePage, setActivePage] = useState<RyanAdminPageType>('stats');
  const [errorCount, setErrorCount] = useState(0);
  const [checking, setChecking] = useState(true);

  // Título/subtítulo do header — cada aba reporta o próprio (ela sabe qual
  // sub-aba interna está ativa, ex: Produtos/Marketing, Vendas/Acesso/...),
  // igual ao padrão já usado pra errorCount.
  const [headerTitle, setHeaderTitle] = useState('Estatísticas');
  const [headerSubtitle, setHeaderSubtitle] = useState('Visão geral da plataforma e engajamento dos usuários.');
  const handleHeaderChange = useCallback((title: string, subtitle: string) => {
    setHeaderTitle(title);
    setHeaderSubtitle(subtitle);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!isAdmin) { navigate('/ryan'); return; }
    setChecking(false);
  }, [isAdmin, loading, navigate]);

  // Auto-exclusão de analytics (Item 3) — liga o opt-out por padrão na
  // PRIMEIRA vez que este navegador vê um admin logado em qualquer tela do
  // painel RYAN (não só Analytics), pra não exigir um clique manual antes de
  // cada teste. Só age se a flag NUNCA foi decidida (chave ausente) —
  // qualquer escolha explícita anterior (ligada ou desligada) nunca é
  // sobrescrita automaticamente de novo, mesmo em logins futuros.
  useEffect(() => {
    if (checking) return;
    if (!isOptOutDecided()) setOptedOut(true);
  }, [checking]);

  useEffect(() => {
    if (checking) return;
    const tok = localStorage.getItem('accessToken');
    fetch('/api/ryan/admin/errors', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : [])
      .then((errs: any[]) => setErrorCount(errs.filter(e => e.status === 'pending').length))
      .catch(() => {});
  }, [checking]);

  if (checking) {
    return (
      <div className="ryan-admin-loading">
        <div className="ryan-admin-spinner" />
      </div>
    );
  }

  return (
    <div className="ryan-admin-page">
      <RyanAdminSidebar
        activePage={activePage}
        onPageChange={setActivePage}
        errorCount={errorCount}
      />
      <main className="ryan-admin-main">
        <RyanAdminHeader title={headerTitle} subtitle={headerSubtitle} />
        <div key={activePage} className="ryan-admin-page-anim">
          {activePage === 'stats'    && <RyanAdminStats onHeaderChange={handleHeaderChange} />}
          {activePage === 'errors'   && (
            <RyanAdminErrors
              onErrorCountChange={setErrorCount}
              onHeaderChange={handleHeaderChange}
            />
          )}
          {activePage === 'sales'    && <RyanAdminHub onHeaderChange={handleHeaderChange} />}
          {activePage === 'accounts' && <RyanAdminAccounts onHeaderChange={handleHeaderChange} />}
        </div>
      </main>
    </div>
  );
};

export default RyanAdminPage;
